import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';
const PRICE_FILTER_MIN_PARAM = 'filter.v.price.gte';
const PRICE_FILTER_MAX_PARAM = 'filter.v.price.lte';

/**
 * @param {string} currency
 * @returns {number}
 */
const getCurrencyPrecision = (currency) =>
  CURRENCY_DECIMALS[currency?.toUpperCase() ?? ''] ?? DEFAULT_CURRENCY_DECIMALS;

/**
 * @param {ParentNode | null} root
 * @returns {string}
 */
const getPriceFacetCurrency = (root) => {
  const status = root?.querySelector?.('[ref="facetStatus"][data-currency]');
  if (!(status instanceof HTMLElement)) return '';
  return status.dataset.currency ?? '';
};

/**
 * @param {string} value
 * @param {number} [precision=DEFAULT_CURRENCY_DECIMALS]
 * @returns {string}
 */
const normalizeMoneyInput = (value, precision = DEFAULT_CURRENCY_DECIMALS) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const cleaned = raw.replace(/[^\d.,' ]+/g, '').replace(/[\s']/g, '');
  if (!cleaned) return '';

  const hasDot = cleaned.includes('.');
  const hasComma = cleaned.includes(',');

  if (precision <= 0) {
    if (hasDot && hasComma) {
      const lastDot = cleaned.lastIndexOf('.');
      const lastComma = cleaned.lastIndexOf(',');
      const decimalSeparator = lastDot > lastComma ? '.' : ',';
      const thousandsSeparator = decimalSeparator === '.' ? ',' : '.';
      const normalized = cleaned.split(thousandsSeparator).join('').replace(decimalSeparator, '.');
      return (normalized.split('.')[0] || '').replace(/[^\d]/g, '');
    }

    if (hasDot || hasComma) {
      const separator = hasDot ? '.' : ',';
      const parts = cleaned.split(separator);

      if (parts.length > 2) return parts.join('').replace(/[^\d]/g, '');

      const [whole = '', fraction = ''] = parts;
      const normalized = fraction.length <= 2 ? whole : whole + fraction;
      return normalized.replace(/[^\d]/g, '');
    }

    return cleaned.replace(/[^\d]/g, '');
  }

  if (hasDot && hasComma) {
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    const decimalSeparator = lastDot > lastComma ? '.' : ',';
    const thousandsSeparator = decimalSeparator === '.' ? ',' : '.';

    return cleaned.split(thousandsSeparator).join('').replace(decimalSeparator, '.');
  }

  if (hasDot || hasComma) {
    const separator = hasDot ? '.' : ',';
    const parts = cleaned.split(separator);

    if (parts.length > 2) return parts.join('');

    const [whole = '', fraction = ''] = parts;
    if (fraction.length === 0) return whole;
    if (fraction.length > precision) return `${whole}${fraction}`;

    return `${whole}.${fraction}`;
  }

  return cleaned;
};

/**
 * @param {string} value
 * @param {number} precision
 * @returns {number}
 */
const parseMoneyInput = (value, precision) => {
  const normalized = normalizeMoneyInput(value, precision);
  if (normalized === '') return NaN;
  return Number(normalized);
};

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    const currencyPrecision = getCurrencyPrecision(getPriceFacetCurrency(this));
    const minPrice = newParameters.get(PRICE_FILTER_MIN_PARAM);

    if (minPrice !== null) {
      const normalizedMinPrice = normalizeMoneyInput(minPrice, currencyPrecision);
      if (normalizedMinPrice === '') newParameters.delete(PRICE_FILTER_MIN_PARAM);
      else newParameters.set(PRICE_FILTER_MIN_PARAM, normalizedMinPrice);
    }

    const maxPrice = newParameters.get(PRICE_FILTER_MAX_PARAM);
    if (maxPrice !== null) {
      const normalizedMaxPrice = normalizeMoneyInput(maxPrice, currencyPrecision);
      if (normalizedMaxPrice === '') newParameters.delete(PRICE_FILTER_MAX_PARAM);
      else newParameters.set(PRICE_FILTER_MAX_PARAM, normalizedMaxPrice);
    }

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    // Remove only the params the facet form manages (every filter/sort input name, plus page
    // and the search query), then re-apply the current selection. This preserves unrelated
    // params such as utm_* / tracking instead of wiping the entire query string on each filter.
    const managedKeys = new Set(['page', SEARCH_QUERY]);
    const form = this.refs.facetsForm;
    if (form) {
      for (const element of form.elements) {
        const name = element.getAttribute('name');
        if (name) managedKeys.add(name);
      }
    }
    for (const key of managedKeys) url.searchParams.delete(key);

    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = async () => {
    const previousURL = window.location.href;
    const previousState = history.state;

    this.#updateURLHash();
    const requestedURL = window.location.href;
    const urlParameters = this.createURLParameters();
    this.dispatchEvent(new FilterUpdateEvent(urlParameters));

    try {
      await this.#updateSection();
    } catch (error) {
      if (window.location.href === requestedURL) {
        history.replaceState(previousState, '', previousURL);
        this.dispatchEvent(new FilterUpdateEvent(new URL(previousURL).searchParams));
      }
      console.error('Failed to update collection filters', error);
    }
  };

  /**
   * Updates the section
   */
  async #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      let renderError;
      let renderPromise = Promise.resolve();
      const renderSection = () => {
        renderPromise = sectionRenderer.renderSection(this.sectionId).then(
          () => undefined,
          (error) => {
            renderError = error;
          }
        );
        return renderPromise;
      };

      await startViewTransition(renderSection, ['product-grid']);
      await renderPromise;
      if (renderError) throw renderError;
    } else {
      await sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  async updateFiltersByURL(url) {
    const previousURL = window.location.href;
    const previousState = history.state;
    const nextURL = new URL(url, window.location.origin);

    history.pushState('', '', nextURL);
    this.dispatchEvent(new FilterUpdateEvent(nextURL.searchParams));

    try {
      await this.#updateSection();
    } catch (error) {
      if (window.location.href === nextURL.href) {
        history.replaceState(previousState, '', previousURL);
        this.dispatchEvent(new FilterUpdateEvent(new URL(previousURL).searchParams));
      }
      console.error('Failed to update collection filters', error);
    }
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) {
      // Remove only this specific name/value pair. The two-argument URLSearchParams.delete is
      // not supported in older Safari/Chromium, where it would drop *all* values for the name
      // (e.g. every selected colour facet), so rebuild the values without that one entry.
      const remainingValues = url.searchParams.getAll(inputElement.name).filter((value) => value !== inputElement.value);
      url.searchParams.delete(inputElement.name);
      for (const value of remainingValues) url.searchParams.append(inputElement.name, value);
    }

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }

}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement | undefined} minRange - The minimum price range input
 * @property {HTMLInputElement | undefined} maxRange - The maximum price range input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.#setRangeStep();
    this.#syncRangeFromInputs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'text') return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  #getCurrencyPrecision() {
    return getCurrencyPrecision(this.#getCurrencyCode());
  }

  #getCurrencyCode() {
    return getPriceFacetCurrency(this.closest('.facets__item') ?? this.closest('details') ?? this);
  }

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#syncRangeFromInputs();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Updates price inputs from the range controls without submitting the filter.
   * @param {InputEvent} event - The input event
   */
  updatePriceInputsFromRange(event) {
    const { minInput, maxInput, minRange, maxRange } = this.refs;
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    let minValue = Number(minRange.value);
    let maxValue = Number(maxRange.value);

    if (Number.isNaN(minValue) || Number.isNaN(maxValue)) return;

    if (minValue > maxValue) {
      if (event.target === minRange) {
        minValue = maxValue;
        minRange.value = String(minValue);
      } else {
        maxValue = minValue;
        maxRange.value = String(maxValue);
      }
    }

    minInput.value = minValue <= Number(minRange.min || 0) ? '' : this.#formatRangeValueForInput(minValue);
    maxInput.value = maxValue >= Number(maxRange.max || 0) ? '' : this.#formatRangeValueForInput(maxValue);

    this.#setMinAndMaxValues();
    this.#updateRangeFill();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const precision = this.#getCurrencyPrecision();
    const value = parseMoneyInput(input.value, precision);
    const min = parseMoneyInput(input.getAttribute('data-min') ?? '', precision);
    const max = parseMoneyInput(input.getAttribute('data-max') ?? '', precision);

    if (Number.isNaN(value)) return;

    if (!Number.isNaN(min) && value < min) input.value = min.toString();
    if (!Number.isNaN(max) && value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  #setRangeStep() {
    const { minRange, maxRange } = this.refs;
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    const precision = this.#getCurrencyPrecision();
    const step = precision <= 0 ? '1' : `0.${'0'.repeat(Math.max(precision - 1, 0))}1`;

    minRange.step = step;
    maxRange.step = step;
  }

  #syncRangeFromInputs() {
    const { minInput, maxInput, minRange, maxRange } = this.refs;
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    const minRangeValue = this.#inputValueToRangeValue(minInput.value, Number(minRange.min || 0));
    const maxRangeValue = this.#inputValueToRangeValue(maxInput.value, Number(maxRange.max || 0));

    minRange.value = String(Math.min(minRangeValue, maxRangeValue));
    maxRange.value = String(Math.max(minRangeValue, maxRangeValue));
    this.#updateRangeFill();
  }

  /**
   * Converts a text input value to a numeric range value
   * @param {string} value - The raw text input value
   * @param {number} fallback - The value to use when parsing fails
   * @returns {number} The parsed range value, or the fallback
   */
  #inputValueToRangeValue(value, fallback) {
    const precision = this.#getCurrencyPrecision();
    const parsedValue = parseMoneyInput(value, precision);
    if (Number.isNaN(parsedValue)) return fallback;

    return parsedValue;
  }

  /**
   * Formats a numeric range value for display in a text input
   * @param {number} value - The range value to format
   * @returns {string} The formatted value
   */
  #formatRangeValueForInput(value) {
    const precision = this.#getCurrencyPrecision();

    if (precision <= 0) return String(Math.round(value));

    return value.toFixed(precision).replace(/\.?0+$/, '');
  }

  #updateRangeFill() {
    const { minRange, maxRange } = this.refs;
    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    const min = Number(minRange.min || 0);
    const max = Number(maxRange.max || 0);
    const minValue = Number(minRange.value);
    const maxValue = Number(maxRange.value);
    const range = max - min;

    if (range <= 0 || Number.isNaN(minValue) || Number.isNaN(maxValue)) return;

    // Write the fill variables onto the `.price-facet__range` wrapper itself. Its inline
    // server-rendered `--price-range-min/max` would otherwise shadow values set on the
    // component, leaving the highlighted track frozen at its initial position.
    const rangeWrapper = minRange.closest('.price-facet__range');
    if (rangeWrapper instanceof HTMLElement) {
      rangeWrapper.style.setProperty('--price-range-min', `${((minValue - min) / range) * 100}%`);
      rangeWrapper.style.setProperty('--price-range-max', `${((maxValue - min) / range) * 100}%`);
    }

    // When both thumbs overlap, the stacked range inputs would otherwise trap pointer
    // interaction on the min thumb. Bring whichever thumb can still move outward to the
    // front so the shopper can always separate them with the mouse/touch.
    const overlap = minValue >= maxValue;
    const minOnTop = !overlap || maxValue >= max;
    minRange.style.zIndex = minOnTop ? '2' : '1';
    maxRange.style.zIndex = minOnTop ? '1' : '2';
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const currency = this.refs.facetStatus?.dataset.currency ?? '';
    const precision = getCurrencyPrecision(currency);
    const normalized = normalizeMoneyInput(value, precision);

    if (normalized === '') return parseInt(fallback, 10);

    const [wholeStr, fractionStr = ''] = normalized.split('.');
    const whole = parseInt(wholeStr || '0', 10);
    if (Number.isNaN(whole)) return parseInt(fallback, 10);

    const fraction = fractionStr.padEnd(2, '0').slice(0, 2);
    const fractionValue = parseInt(fraction || '0', 10);

    return whole * 100 + (Number.isNaN(fractionValue) ? 0 : fractionValue);
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
